import { Controller } from '@hotwired/stimulus';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import 'tippy.js/themes/light-border.css';

// Family tree (#4480). The tree itself is rendered server side; this controller only
// - scales the fixed-size canvas to the available width ("fit") or shows it 1:1 centred on the reference person
// - copies a member's details into the shared modal, which lives OUTSIDE the scaled canvas
//   (position: fixed breaks inside a transformed ancestor), and binds the (i) tooltips of the copy (#4492) -
//   the page-wide tooltips controller only sees what exists on load
const MIN_READABLE_SCALE = 0.6;

export default class extends Controller {
  static targets = [
    'viewport',
    'frame',
    'canvas',
    'toggle',
    'details',
    'modalBody',
  ];
  static outlets = ['modal'];
  static values = { width: Number, height: Number, open: String };

  connect() {
    this.onResize = () => this.apply();
    window.addEventListener('resize', this.onResize);

    // Too small to read when fitted (phones): start 1:1 on the reference person instead
    this.fit = this.fitScale >= MIN_READABLE_SCALE;
    this.apply();
    if (!this.fit) this.centerOnReference();
  }

  // Coming back from linking / creating a profile: show that member again. The outlet callback can fire
  // before the modal controller ran its own connect() (open() would then do nothing), hence the extra tick.
  modalOutletConnected() {
    if (!this.openValue || this.openedOnLoad) return;

    this.openedOnLoad = true;
    setTimeout(() => this.openDetails(this.openValue), 0);
  }

  disconnect() {
    window.removeEventListener('resize', this.onResize);
    this.destroyInfoTips();
  }

  get fitScale() {
    return Math.min(1, this.viewportTarget.clientWidth / this.widthValue);
  }

  toggleFit() {
    this.fit = !this.fit;
    this.apply();
    if (!this.fit) this.centerOnReference();
  }

  apply() {
    const scale = this.fit ? this.fitScale : 1;

    this.canvasTarget.style.transform = scale === 1 ? '' : `scale(${scale})`;
    // The transform does not change the layout box, so the frame carries the scaled size
    this.frameTarget.style.width = `${this.widthValue * scale}px`;
    this.frameTarget.style.height = `${this.heightValue * scale}px`;

    if (this.hasToggleTarget) {
      const button = this.toggleTarget;
      button.classList.toggle('hidden', this.fitScale === 1);
      button.textContent = this.fit
        ? button.dataset.actualLabel
        : button.dataset.fitLabel;
    }
  }

  centerOnReference() {
    const reference = this.canvasTarget.querySelector('[data-role="self"]');
    if (!reference) return;

    const card = reference.parentElement;
    const viewport = this.viewportTarget;
    viewport.scrollLeft =
      card.offsetLeft + card.offsetWidth / 2 - viewport.clientWidth / 2;
  }

  showDetails(event) {
    // Links inside a card (profile reference) keep working
    if (event.target.closest('a')) return;

    this.openDetails(event.currentTarget.dataset.role, event);
  }

  openDetails(role, event) {
    const template = this.detailsTargets.find((t) => t.dataset.role === role);
    if (!template || !this.hasModalOutlet) return;

    this.destroyInfoTips();
    this.modalBodyTarget.replaceChildren(template.content.cloneNode(true));
    this.bindInfoTips();
    this.modalOutlet.open(event);
  }

  bindInfoTips() {
    const buttons = this.modalBodyTarget.querySelectorAll(
      '[data-family-tree-info]',
    );
    this.infoTips = tippy(buttons, {
      content: (button) => button.dataset.familyTreeInfo,
      arrow: true,
      theme: 'light-border',
      maxWidth: 400,
      animation: 'scale',
    });
  }

  destroyInfoTips() {
    (this.infoTips || []).forEach((instance) => instance.destroy());
    this.infoTips = [];
  }
}
